/**
 * TDM Filling Option
 * Adds an interior cushion line item when selected.
 */
(function () {
  'use strict';

  const TDMFilling = {
    selectors: {
      container: '.tdm-filling-option',
      checkbox: '.tdm-filling-checkbox',
      confirmation: '.tdm-filling-confirmation',
      infoBtn: '.tdm-filling-info-btn',
      tooltip: '.tdm-filling-tooltip',
      form: 'form[data-type="add-to-cart-form"]',
      productFormComponent: 'product-form-component',
      quantityInput: '.quantity-selector input[type="number"], input[name="quantity"]',
      addToCartComponent: 'add-to-cart-component',
      addButton: '[ref="addToCartButton"]',
      stickyButton: '#tdm-sticky-add-btn',
      cartItemsComponent: 'cart-items-component',
    },

    state: {
      isSubmitting: false,
      tooltipBound: false,
    },

    init() {
      const containers = document.querySelectorAll(this.selectors.container);
      if (!containers.length) return;

      containers.forEach((container) => this.bindContainer(container));
      this.bindGlobal();
    },

    bindContainer(container) {
      const checkbox = container.querySelector(this.selectors.checkbox);
      if (checkbox) {
        checkbox.addEventListener('change', (e) => this.onCheckboxChange(e, container));
      }

      const infoBtn = container.querySelector(this.selectors.infoBtn);
      if (infoBtn) {
        infoBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          const tooltip = infoBtn.querySelector(this.selectors.tooltip);
          if (!tooltip) return;
          const isVisible = tooltip.classList.toggle('is-visible');
          if (isVisible && container.closest('.quick-add-modal__content')) {
            const rect = infoBtn.getBoundingClientRect();
            tooltip.style.position = 'fixed';
            tooltip.style.top = (rect.top - tooltip.offsetHeight - 8) + 'px';
            tooltip.style.left = Math.max(8, rect.left - 100) + 'px';
          }
        });
      }
    },

    bindGlobal() {
      if (!this.state.tooltipBound) {
        document.addEventListener('click', (e) => {
          if (!e.target.closest(this.selectors.infoBtn)) {
            document
              .querySelectorAll(`${this.selectors.tooltip}.is-visible`)
              .forEach((t) => t.classList.remove('is-visible'));
          }
        });
        this.state.tooltipBound = true;
      }

      document.addEventListener('click', (e) => {
        const stickyBtn = e.target.closest(this.selectors.stickyButton);
        if (!stickyBtn) return;

        const productFormComponent = document.querySelector(this.selectors.productFormComponent);
        if (!this.shouldAddFillingForForm(productFormComponent)) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const container = this.getContainerForForm(productFormComponent);
        this.addBothToCart(productFormComponent, container);
      });

      document.addEventListener('variant:change', (e) => this.onVariantUpdate(e));
      document.addEventListener('variant:changed', (e) => this.onVariantUpdate(e));
    },

    onVariantUpdate(e) {
      const variant = e?.detail?.variant;
      if (!variant) return;

      document.querySelectorAll(this.selectors.container).forEach((container) => {
        container.dataset.mainVariantId = variant.id;
        if (variant.title) container.dataset.mainVariantTitle = variant.title;
      });
    },

    onCheckboxChange(e, container) {
      container.classList.toggle('is-checked', e.target.checked);
      const confirmation = container.querySelector(this.selectors.confirmation);
      if (confirmation) {
        confirmation.style.display = e.target.checked ? 'flex' : 'none';
      }
    },

    getContainerForForm(productFormComponent = null) {
      if (productFormComponent) {
        const details = productFormComponent.closest('.product-details');
        return details?.querySelector(this.selectors.container) || null;
      }
      return document.querySelector(this.selectors.container);
    },

    shouldAddFilling(container) {
      if (!container) return false;
      const checkbox = container.querySelector(this.selectors.checkbox);
      return !!checkbox?.checked && !checkbox.disabled;
    },

    shouldAddFillingForForm(productFormComponent = null) {
      const container = this.getContainerForForm(productFormComponent);
      return this.shouldAddFilling(container);
    },

    getQuantity(productFormComponent = null) {
      const scope = productFormComponent || document;
      const input = scope.querySelector(this.selectors.quantityInput);
      return parseInt(input?.value, 10) || 1;
    },

    getMainVariantId(productFormComponent = null) {
      const scope = productFormComponent || document;
      return scope.querySelector('input[name="id"]')?.value;
    },

    async ensureFillingVariantId(container) {
      const datasetVariantId = container?.dataset?.fillingVariantId;
      if (datasetVariantId) return datasetVariantId;

      const sku = container?.dataset?.fillingSku;
      if (!sku) return null;

      return await this.findVariantIdBySku(sku);
    },

    async findVariantIdBySku(sku) {
      if (!sku) return null;

      try {
        const res = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(sku)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last`
        );
        if (!res.ok) return null;
        const data = await res.json();
        const products = data?.resources?.results?.products || [];

        for (const product of products) {
          if (!product?.handle) continue;
          const productRes = await fetch(`/products/${product.handle}.js`);
          if (!productRes.ok) continue;
          const productData = await productRes.json();
          const match = productData?.variants?.find((variant) => variant?.sku === sku);
          if (match?.id) return String(match.id);
        }
      } catch (e) {
        return null;
      }

      return null;
    },

    handleProductFormSubmit(productFormComponent, event) {
      if (!this.shouldAddFillingForForm(productFormComponent)) return false;

      if (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.tdmFillingHandled = true;
      }

      const container = this.getContainerForForm(productFormComponent);
      this.addBothToCart(productFormComponent, container);
      return true;
    },

    async addBothToCart(productFormComponent = null, container = null) {
      if (this.state.isSubmitting) return;
      this.state.isSubmitting = true;

      const resolvedProductFormComponent =
        productFormComponent || document.querySelector(this.selectors.productFormComponent);
      const resolvedContainer = container || this.getContainerForForm(resolvedProductFormComponent);
      const mainVariantId = this.getMainVariantId(resolvedProductFormComponent);
      const quantity = this.getQuantity(resolvedProductFormComponent);
      const form = resolvedProductFormComponent?.querySelector(this.selectors.form);

      if (!mainVariantId || !form) {
        this.state.isSubmitting = false;
        return;
      }

      const fillingVariantId = await this.ensureFillingVariantId(resolvedContainer);
      if (!fillingVariantId) {
        this.state.isSubmitting = false;
        this.showError('Filling variant unavailable.');
        return;
      }

      const linkId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const mainTitle = resolvedContainer?.dataset?.mainProductTitle || '';
      const mainVariantTitle = resolvedContainer?.dataset?.mainVariantTitle || '';
      const mainProductId = resolvedContainer?.dataset?.mainProductId || '';

      this.animateButton(resolvedProductFormComponent);

      try {
        const cartItemsComponents = document.querySelectorAll(this.selectors.cartItemsComponent);
        const sectionIds = [];
        cartItemsComponents.forEach((item) => {
          if (item.dataset?.sectionId) sectionIds.push(item.dataset.sectionId);
        });

        const formData1 = new FormData(form);
        formData1.set('id', mainVariantId);
        formData1.set('quantity', quantity.toString());
        formData1.append('properties[_tdm_link_id]', linkId);
        formData1.append('properties[_tdm_filling]', 'yes');
        formData1.append('properties[_tdm_filling_variant]', fillingVariantId);
        if (sectionIds.length) formData1.append('sections', sectionIds.join(','));

        const response1 = await fetch(Theme.routes.cart_add_url || '/cart/add.js', {
          method: 'POST',
          body: formData1,
          headers: { Accept: 'text/html' },
        });
        const result1 = await response1.json();
        if (result1.status) throw new Error(result1.description || result1.message);

        const formData2 = new FormData();
        formData2.append('id', fillingVariantId);
        formData2.append('quantity', quantity.toString());
        formData2.append('properties[_tdm_link_id]', linkId);
        formData2.append('properties[_tdm_for_product]', mainTitle);
        formData2.append('properties[_tdm_for_variant]', mainVariantTitle);
        formData2.append('properties[_tdm_for_product_id]', mainProductId);
        if (sectionIds.length) formData2.append('sections', sectionIds.join(','));

        const response2 = await fetch(Theme.routes.cart_add_url || '/cart/add.js', {
          method: 'POST',
          body: formData2,
          headers: { Accept: 'text/html' },
        });
        const result2 = await response2.json();
        if (result2.status) throw new Error(result2.description || result2.message);

        this.dispatchCartUpdate(result2);
      } catch (error) {
        this.showError(error.message || "Erreur lors de l'ajout du garnissage.");
      } finally {
        this.state.isSubmitting = false;
      }
    },

    dispatchCartUpdate(result) {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: result,
            sourceId: 'tdm-filling',
            data: {
              source: 'tdm-filling',
              sections: result.sections,
            },
          },
        })
      );

      if (result.sections) {
        Object.entries(result.sections).forEach(([id, html]) => {
          const selectors = [`#shopify-section-${id}`, `[data-section-id="${id}"]`];
          for (const selector of selectors) {
            const el = document.querySelector(selector);
            if (el) {
              el.innerHTML = html;
              break;
            }
          }
        });
      }

      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer) {
        if (typeof cartDrawer.open === 'function') cartDrawer.open();
        else if (typeof cartDrawer.showDialog === 'function') cartDrawer.showDialog();
      }
    },

    animateButton(productFormComponent = null) {
      const scope = productFormComponent || document;
      const addToCartComponent = scope.querySelector(this.selectors.addToCartComponent);
      const button = scope.querySelector(this.selectors.addButton);

      if (button) {
        button.dataset.added = 'true';
        setTimeout(() => {
          button.removeAttribute('data-added');
        }, 2500);
      }

      if (addToCartComponent && typeof addToCartComponent.animateAddToCart === 'function') {
        addToCartComponent.animateAddToCart();
      }
    },

    showError(message) {
      document.querySelector('.tdm-filling-error')?.remove();
      const toast = document.createElement('div');
      toast.className = 'tdm-filling-error';
      toast.setAttribute('role', 'alert');
      toast.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
        <span>${message}</span>
      `;
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 5000);
    },
  };

  window.TDMFilling = TDMFilling;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => TDMFilling.init());
  } else {
    TDMFilling.init();
  }

  document.addEventListener('shopify:section:load', () => TDMFilling.init());
})();
